import React, { useEffect, useState, useCallback, useMemo } from "react";
import { inventoryApi } from "../services/api";
import AddItemForm from "../components/AddItemForm";
import InventoryTable from "../components/InventoryTable";
import "./Page.css";
import "./InventoryPage.css";

const SORT_OPTIONS = [
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
  { value: "status", label: "Status" },
  { value: "category", label: "Category" },
  { value: "updated", label: "Recently Updated" },
];

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "enough", label: "🟢 Enough" },
  { value: "running_low", label: "🟡 Running Low" },
  { value: "finished", label: "🔴 Finished" },
];

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await inventoryApi.getAll();
      setItems(data);
      setError(null);
    } catch {
      setError("Failed to load inventory.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const categories = useMemo(() => {
    const cats = [...new Set(items.map((i) => i.category).filter(Boolean))].sort();
    return cats;
  }, [items]);

  const filteredItems = useMemo(() => {
    let result = [...items];

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((i) =>
        i.name.toLowerCase().includes(q) ||
        (i.category && i.category.toLowerCase().includes(q))
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((i) => i.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== "all") {
      result = result.filter((i) => i.category === categoryFilter);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case "name_asc": return a.name.localeCompare(b.name);
        case "name_desc": return b.name.localeCompare(a.name);
        case "status": {
          const order = { finished: 0, running_low: 1, enough: 2 };
          return (order[a.status] ?? 3) - (order[b.status] ?? 3);
        }
        case "category": return (a.category || "").localeCompare(b.category || "");
        case "updated": return new Date(b.updated_at) - new Date(a.updated_at);
        default: return 0;
      }
    });

    return result;
  }, [items, search, sortBy, statusFilter, categoryFilter]);

  const handleStatusChange = async (id, status, quantity) => {
    try {
      const updated = await inventoryApi.updateStatus(id, status, quantity);
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    } catch {
      setError("Failed to update status.");
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const updated = await inventoryApi.update(id, data);
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to update item.";
      setError(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await inventoryApi.delete(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      setError("Failed to delete item.");
    }
  };

  const handleAdd = (newItem) => {
    setItems((prev) => [...prev, newItem]);
  };

  const hasFilters = search || statusFilter !== "all" || categoryFilter !== "all" || sortBy !== "name_asc";

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Inventory</h1>
          <p className="page-sub">
            {filteredItems.length} of {items.length} item{items.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "✕ Done" : "+ Add Item"}
        </button>
      </div>

      {showForm && <AddItemForm onAdd={handleAdd} onCancel={() => setShowForm(false)} />}
      {error && <div className="error-banner" onClick={() => setError(null)}>{error} ✕</div>}

      {/* Filters Bar */}
      <div className="inv-filters">
        <input
          type="text"
          placeholder="🔍 Search items…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="inv-search"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="inv-select">
          {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="inv-select">
          <option value="all">All Categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="inv-select">
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {hasFilters && (
          <button className="inv-clear-btn" onClick={() => {
            setSearch(""); setStatusFilter("all");
            setCategoryFilter("all"); setSortBy("name_asc");
          }}>
            ✕ Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading inventory…</div>
      ) : (
        <InventoryTable
          items={filteredItems}
          onStatusChange={handleStatusChange}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
